/**
 * UStVA snapshots (backend modules/Accounting, S4): one row per owner, period and
 * version, computed only from the effects of the assigned recognitions.
 *
 * Resource: /api/accounting-vat-return (index, show, store = compute) plus the
 * workflow actions recompute / review / reopen / submit / release-input-tax and
 * GET xml / versions.
 */
import type { OwnerResource } from "../../owner";
import type {
  FilingPeriodEnum,
  TaxationMethodEnum,
} from "../../accounting-owner-tax-settings";

export enum VatReturnStatusEnum {
  Draft = "draft",
  Reviewed = "reviewed",
  Submitted = "submitted",
  Accepted = "accepted",
  Rejected = "rejected",
  Superseded = "superseded",
}

export enum VatReturnWarningCodeEnum {
  TaxationMethodMissing = "taxation_method_missing",
  OpenReviewItems = "open_review_items",
  PendingDocuments = "pending_documents",
  RevenuePlausibility = "revenue_plausibility",
  LateRecognitions = "late_recognitions",
  ElsterProfileIncomplete = "elster_profile_incomplete",
  IncompleteTaxYearMapping = "incomplete_tax_year_mapping",
  // laufende Hinweise, beim Anzeigen ermittelt (nicht Teil des Snapshots)
  ElsterConfigurationIncomplete = "elster_configuration_incomplete",
  FilingDeadlineOverdue = "filing_deadline_overdue",
  EricHints = "eric_hints",
}

export type VatReturnWarningSeverity = "info" | "warning" | "error";

/** One input-tax item without a document attached/assigned in the ERP yet. */
export type VatReturnPendingDocumentItem = {
  vat_entry_id: string;
  tax_date: string;
  /** M2: ohne bestätigten Zeitpunkt nur vorläufig in diesem Zeitraum, nicht freigebbar */
  tax_date_confirmed?: boolean;
  invoice_date?: string | null;
  accounting_posting_id: string | null;
  posting_number: string | null;
  description: string | null;
  external_document_number: string | null;
  supplier_id: string | null;
  gross_amount: string;
  vat_amount: string;
  vat_percent: string;
  status: string | null;
};

export type VatReturnWarning = {
  code: VatReturnWarningCodeEnum;
  severity: VatReturnWarningSeverity;
  /** Verhindert dieser Hinweis die Übermittlung an ELSTER? Getrennt vom Schweregrad. */
  blocks_submission: boolean;
  message: string;
  context: Record<string, unknown> & {
    items?: VatReturnPendingDocumentItem[];
    count?: number;
    vat_amount?: string;
  };
};

export type VatReturnLine = {
  code: string;
  amount_raw: string;
  amount_reported: string;
  breakdown: Record<string, number> | null;
};

export type VatReturnReleasedInputTax = {
  vat_entry_id: string;
  released_by: string | null;
  released_by_name: string | null;
  released_at: string;
  released_amount: string;
  confirmation_text_version: number;
  release_note: string | null;
};

/**
 * Ein Versuch, die Voranmeldung beim ERiC-Gateway prüfen zu lassen oder zu übertragen
 * (Tabelle accounting_elster_operations, append-only). `provider` und `simulated`
 * übernehmen die Angabe des Gateways; ohne auswertbare Antwort sind beide null.
 */
export type VatReturnElsterOperation = {
  id: string;
  /** UStVA oder ZM */
  report_type?: "UStVA" | "ZM";
  action: "validate" | "transmit";
  /** started | local_aborted | valid | invalid | accepted | submitted | rejected | failed | not_configured */
  outcome: string;
  /** false: lokal abgebrochen, der Gateway wurde nicht angefragt; null: Eintrag vor dem 01.10.2026 */
  gateway_called: boolean | null;
  /** preconditions_missing | xml_preparation_failed */
  abort_reason: string | null;
  /** leer: begonnen, Ausgang nicht protokolliert */
  completed_at: string | null;
  provider: "simulation" | "eric" | null;
  simulated: boolean | null;
  /** gesetzt = Testbetrieb, leer = Echtbetrieb */
  test_marker: string | null;
  report_version: number;
  source_fingerprint: string | null;
  /** SHA-256 des an den Gateway gegebenen XML; bindet das Ergebnis an die geprüften Nutzdaten */
  payload_sha256?: string | null;
  /** geprüfte bzw. gesendete XML-Datei archiviert (seit 05.10.2026) */
  has_payload?: boolean;
  /** Entsprach der gespeicherte Stand beim Versuch dem aktuellen Datenstand? */
  data_current: boolean;
  transfer_ticket: string | null;
  eric_version: string | null;
  messages: string[];
  error: string | null;
  requested_by: string | null;
  requested_by_name: string | null;
  requested_at: string;
};

export interface AccountingVatReturnResource {
  id: string;
  owner_id: string;
  period_year: number;
  period_month: number;
  period_type: FilingPeriodEnum;
  period_label: string;
  period_start: string;
  period_end: string;
  version: number;
  is_correction: boolean;
  status: VatReturnStatusEnum;
  taxation_method: TaxationMethodEnum | null;
  computed_at: string | null;
  computed_by: string | null;
  source_fingerprint: string | null;
  warnings: VatReturnWarning[];
  recognitions_count: number;
  pending_document_count: number;
  /** Aktueller Stand, nur bei Entwürfen; weicht er ab, ist der Entwurf veraltet. */
  pending_document_count_current: number | null;
  /** Die Menge der Vorgänge ohne Beleg weicht vom berechneten Entwurf ab. */
  pending_documents_changed: boolean | null;
  pending_document_vat: string;
  released_input_tax: VatReturnReleasedInputTax[];
  due_date: string | null;
  reviewed_by: string | null;
  reviewed_by_name?: string | null;
  reviewed_at: string | null;
  submitted_by: string | null;
  submitted_by_name?: string | null;
  submitted_at: string | null;
  transfer_ticket: string | null;
  transmitter_response: Record<string, unknown> | null;
  /** Unternehmens- und Steuerangaben zum Zeitpunkt der Berechnung (eingefroren); null bei Meldungen vor dem 02.10.2026 */
  reporting_party?: {
    name: string | null;
    tax_number: string | null;
    w_idnr: string | null;
    street: string | null;
    house_number: string | null;
    postal_code: string | null;
    city: string | null;
    country_code: string | null;
  } | null;
  has_protocol: boolean;
  supersedes_id: string | null;
  superseded_at: string | null;
  lines?: VatReturnLine[];
  elster_operations?: VatReturnElsterOperation[];
  owner?: OwnerResource | null;
  release_confirmation_text: string;
  release_confirmation_text_version: number;
  created_at: string | null;
  updated_at: string | null;
}

export type ComputeAccountingVatReturnRequestBody = {
  owner_id: string;
  period_year: number;
  period_month: number;
  /** Explicit request to create a correction of a transmitted period. */
  new_version?: boolean;
};

export type ReleaseInputTaxRequestBody = {
  vat_entry_ids: string[];
  /** The person accepted `release_confirmation_text`. */
  confirmed: boolean;
  note?: string | null;
};
